import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Receipt, Truck, Landmark, Check, ArrowRight, Loader2, FileDown, Pencil } from "lucide-react";
import { DnItemsEditor } from "@/components/DnItemsEditor";

const STEPS = [
  { key: "quotation", label: "Quotation", icon: FileText, pdf: (s) => `/quotations/${s.id}/pdf` },
  { key: "invoice", label: "Invoice", icon: Receipt, pdf: (s) => `/invoices/${s.id}/pdf` },
  { key: "delivery_note", label: "Delivery Note", icon: Truck, pdf: (s) => `/delivery-notes/${s.id}/pdf` },
  { key: "statement", label: "Statement", icon: Landmark, pdf: (s) => `/customers/${s.customer_id}/statement/pdf` },
];

export function FlowStrip({ kind, id, compact = false, onChanged }) {
  const [flow, setFlow] = useState(null);
  const [dialog, setDialog] = useState(null);
  const navigate = useNavigate();
  const load = () => api.get(`/flows/for/${kind}/${id}`).then(({ data }) => setFlow(data)).catch(() => setFlow(null));
  useEffect(() => { load(); }, [kind, id]);
  if (!flow) return null;

  const run = async (step, payload = {}) => {
    try {
      const { data } = await api.post("/flows/next", { kind, id, step, payload });
      setFlow(data.flow); setDialog(null); onChanged?.();
      if (step === "statement") window.open(`${API}${data.created.pdf_url}`, "_blank");
      if (step === "invoice") { toast.success(`Draft invoice ${data.created.number} created — edit and finalise it`); navigate(`/invoices/${data.created.id}/edit`); return; }
      toast.success(step === "statement" ? "Statement generated — flow complete" : `${STEPS.find((s) => s.key === step).label} ${data.created.number} created`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const startNext = (step) => (step === "delivery_note") ? setDialog(step) : run(step);

  return (
    <div data-testid="flow-strip">
      <div className={`flex items-stretch ${compact ? "gap-1" : "gap-2"} overflow-x-auto`}>
        {STEPS.map((s, i) => {
          const st = flow.steps[s.key];
          const isNext = flow.next_step === s.key;
          const Icon = s.icon;
          return (
            <div key={s.key} className="flex items-center gap-1 min-w-0">
              <div className={`rounded-md border px-2.5 py-1.5 flex items-center gap-2 ${st.done ? "border-emerald-600 bg-emerald-50 text-emerald-800" : isNext ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground"}`} data-testid={`flow-step-${s.key}`}>
                {st.done ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                <div className="leading-tight">
                  <div className="text-[10px] uppercase tracking-wide">{s.label}</div>
                  {st.done && s.key !== "statement" && <div className="text-xs font-semibold">{st.number}{st.status === "Draft" ? " · Draft" : ""}</div>}
                  {st.done && s.key === "statement" && <div className="text-xs font-semibold">Issued</div>}
                  {st.done && s.key === "invoice" && st.customer_po_number && <div className="text-[10px]">Client PO {st.customer_po_number}</div>}
                </div>
                {st.done && s.key === "invoice" && <button onClick={() => navigate(`/invoices/${st.id}/edit`)} title={st.status === "Draft" ? "Edit & finalise" : "Edit invoice"} data-testid="flow-edit-invoice"><Pencil className="h-3.5 w-3.5 opacity-70 hover:opacity-100" /></button>}
                {st.done && s.pdf && (s.key !== "statement" || st.customer_id) && <a href={`${API}${s.pdf(st)}`} target="_blank" rel="noreferrer" title="Open PDF" data-testid={`flow-pdf-${s.key}`}><FileDown className="h-3.5 w-3.5 opacity-70 hover:opacity-100" /></a>}
                {isNext && <Button size="sm" className="h-6 px-2 text-[11px] bg-primary hover:bg-[#C81219] text-white" onClick={() => startNext(s.key)} data-testid={`flow-create-${s.key}`}>Create</Button>}
              </div>
              {i < STEPS.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
            </div>
          );
        })}
      </div>
      {dialog === "delivery_note" && <DnDialog source="invoice" refId={flow.steps.invoice?.id} onClose={() => setDialog(null)} onSubmit={(p) => run("delivery_note", p)} />}
    </div>
  );
}

export function DnDialog({ source = "invoice", refId, onClose, onSubmit }) {
  const [f, setF] = useState({ delivery_date: new Date().toISOString().slice(0, 10), notes: "", items: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (refId) api.get(`/delivery-notes/preview?source=${source}&ref_id=${refId}`).then(({ data }) => setF((p) => ({ ...p, items: data.items }))).catch(() => {});
  }, [source, refId]);
  const submit = async () => { setBusy(true); await onSubmit(f); setBusy(false); };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle className="font-display text-xl">Delivery Note from {source}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Delivery date</Label><Input type="date" value={f.delivery_date} onChange={(e) => setF({ ...f, delivery_date: e.target.value })} data-testid="flow-dn-date" /></div>
          <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Notes (optional)</Label><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="flow-dn-notes" /></div>
          <div className="col-span-2 space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Items — edit products & quantities to match what is in stock</Label>
            <DnItemsEditor items={f.items} onChange={(items) => setF({ ...f, items })} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button disabled={busy} onClick={submit} className="bg-primary hover:bg-[#C81219] text-white" data-testid="flow-dn-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Delivery Note"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
